import { GlobalTagBadge } from './GlobalTagBadge';
import { useState } from 'react';
import { Check, Pencil, Trash2, X } from 'lucide-react';

import { Button } from '@/marketing/components/ui/button';
import { Checkbox } from '@/marketing/components/ui/checkbox';
import { Input } from '@/marketing/components/ui/input';
import {
  type CampaignInfluencerTag,
  isDefaultCampaignInfluencerTagName,
  MAX_CAMPAIGN_INFLUENCER_TAG_NAME_LENGTH,
} from '@/marketing/models/campaignInfluencerTag';

interface CampaignInfluencerTagManagerRowProps {
  tag: CampaignInfluencerTag;
  isAssigned: boolean;
  isMutating: boolean;
  onToggleAssignment: (tagId: string) => void;
  onRename: (tagId: string, name: string) => void;
  onDelete: (tagId: string) => void;
}

export function CampaignInfluencerTagManagerRow({
  tag,
  isAssigned,
  isMutating,
  onToggleAssignment,
  onRename,
  onDelete,
}: CampaignInfluencerTagManagerRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(tag.name);
  const accessibleName = tag.campaignId ? tag.name : `${tag.name} (Global)`;
  const isReservedGlobalName = !tag.campaignId && isDefaultCampaignInfluencerTagName(draftName);

  const handleStartEdit = () => {
    setDraftName(tag.name);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftName(tag.name);
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    if (isMutating || isReservedGlobalName) return;

    const trimmedName = draftName.trim();
    if (trimmedName.length === 0 || trimmedName === tag.name) {
      handleCancelEdit();
      return;
    }

    if (
      !tag.campaignId &&
      !window.confirm(`Rename global tag "${tag.name}"? This changes its name in every campaign that uses it.`)
    )
      return;

    onRename(tag.tagId, trimmedName);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-px-1 tw-py-1">
          <Input
            autoFocus
            value={draftName}
            aria-invalid={isReservedGlobalName}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSaveEdit();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                handleCancelEdit();
              }
            }}
            maxLength={MAX_CAMPAIGN_INFLUENCER_TAG_NAME_LENGTH}
            className="tw-h-7 tw-text-[13px]"
          />
          <Button
            variant="ghost"
            size="sm"
            className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-p-0 tw-text-emerald-600"
            disabled={isMutating || isReservedGlobalName}
            onClick={handleSaveEdit}
            aria-label="Save tag name"
          >
            <Check className="tw-h-3.5 tw-w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-p-0 tw-text-muted-foreground"
            onClick={handleCancelEdit}
            aria-label="Cancel rename"
          >
            <X className="tw-h-3.5 tw-w-3.5" />
          </Button>
        </div>
        {isReservedGlobalName && (
          <p className="tw-m-0 tw-px-2 tw-pb-1 tw-text-[11px] tw-text-destructive">Default tags cannot be global.</p>
        )}
      </div>
    );
  }

  return (
    <div className="tw-group tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 hover:tw-bg-accent">
      <label className="tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-gap-2 tw-truncate tw-text-[13px]">
        <Checkbox
          aria-label={tag.campaignId ? tag.name : `${tag.name} Global`}
          checked={isAssigned}
          disabled={isMutating}
          onCheckedChange={() => onToggleAssignment(tag.tagId)}
        />
        <span className="tw-min-w-0 tw-flex-1 tw-truncate">{tag.name}</span>
        {!tag.campaignId && <GlobalTagBadge />}
      </label>
      <Button
        variant="ghost"
        size="sm"
        className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-p-0 tw-text-muted-foreground tw-opacity-0 focus-visible:tw-opacity-100 group-hover:tw-opacity-100"
        disabled={isMutating}
        onClick={handleStartEdit}
        aria-label={`Rename ${accessibleName}`}
      >
        <Pencil className="tw-h-3 tw-w-3" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-p-0 tw-text-muted-foreground tw-opacity-0 focus-visible:tw-opacity-100 hover:tw-text-rose-600 group-hover:tw-opacity-100"
        disabled={isMutating}
        onClick={() => {
          if (
            window.confirm(
              tag.campaignId
                ? `Delete tag "${tag.name}"? It will be removed from creators in this campaign.`
                : `Delete global tag "${tag.name}"? It will be removed from every campaign that uses it.`,
            )
          ) {
            onDelete(tag.tagId);
          }
        }}
        aria-label={`Delete ${accessibleName}`}
      >
        <Trash2 className="tw-h-3 tw-w-3" />
      </Button>
    </div>
  );
}
